import { SortDirection } from '@bp/planung-graphql-types';
import { Select, SelectOptionType } from '@bp/ui-components';
import dayjs, { Dayjs } from 'dayjs';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { MultiValue } from 'react-select';
import { useCreateSelectOptions } from '../../../hooks/useCreateSelectOptions';
import { useLoadBasicData } from '../../../hooks/useLoadBasicData';
import { useMemorizedCacheTag } from '../../../hooks/useMemorizedCacheTag';
import { SubstitutionDataEntry } from '../../../pages/Substitutions/Plan/PlanSubstitutions/utils';
import { use_EventsQuery, use_SchoolEventsQuery } from '../../../types/planung-graphql-client-defs';
import { isFirstBetweenSecondAndThird } from '../../../utils/dateCalculations';
import { useSubstitutionStore } from '../SubstitutionProvider';
import styles from './SubstitutionRoomsSelect.module.scss';

type SubstitutionRoomsSelectProps = {
  originalRoomUuids: string[];
  eventRoomUuids: string[];
  eventStart: Dayjs;
  eventEnd: Dayjs;
  error: string | null;
  onChange: (uuids: string[]) => void;
  disabled?: boolean;
};

export const SubstitutionRoomsSelect = ({
  originalRoomUuids,
  eventRoomUuids,
  eventStart,
  eventEnd,
  error,
  onChange,
  disabled,
}: SubstitutionRoomsSelectProps) => {
  const { t } = useTranslation();
  const eventContext = useMemorizedCacheTag('EVENT');

  const { selectedDay } = useSubstitutionStore();
  const selectedDayStartString = dayjs(selectedDay).startOf('day').utc().toISOString();
  const selectedDayEndString = dayjs(selectedDay).endOf('day').utc().toISOString();

  const { roomsData } = useLoadBasicData({ pause: false });

  const [{ data: eventData }] = use_EventsQuery({
    variables: {
      options: {
        sort: [{ isSchoolEvent: SortDirection.Desc }, { start: SortDirection.Asc }],
      },
      where: {
        OR: [
          {
            start_GTE: selectedDayStartString,
            end_LTE: selectedDayEndString,
          },
          {
            start_LTE: selectedDayStartString,
            end_GTE: selectedDayEndString,
          },
          {
            AND: [
              {
                start_GTE: selectedDayStartString,
                start_LTE: selectedDayEndString,
              },
            ],
          },
        ],
      },
    },
    context: eventContext,
  });

  const availableRooms: SubstitutionDataEntry[] = useMemo(() => {
    const rooms: SubstitutionDataEntry[] = [];

    // find rooms that are taken at the same time
    const sameTimeRoomUuids =
      eventData?.events
        .filter((event) => isFirstBetweenSecondAndThird(event.start, eventStart, eventEnd, 'minutes'))
        .flatMap((event) => {
          return event.rooms.map((room) => room.uuid);
        }) ?? [];

    // get all rooms
    const allRoomUuids = roomsData.rooms.filter((room) => room.classroom).map((room) => room.uuid);
    // substract taken rooms
    const availableRoomUuids = allRoomUuids.filter((uuid) => !sameTimeRoomUuids.includes(uuid));
    // push rooms from current event
    originalRoomUuids.forEach((uuid) => availableRoomUuids.push(uuid));

    // map available rooms
    availableRoomUuids.forEach((uuid) => {
      const room = roomsData.rooms.find((room) => room.uuid === uuid);
      if (room) {
        rooms.push({
          uuid: room.uuid,
          name: room.name,
          shortName: room.roomNumber ?? '',
        });
      }
    });

    return rooms.sort((a, b) => {
      return a.name.localeCompare(b.name, undefined, {
        numeric: true,
        sensitivity: 'base',
      });
    });
  }, [eventData?.events, roomsData.rooms, originalRoomUuids, eventStart, eventEnd]);

  const roomOptions = useCreateSelectOptions(availableRooms, 'uuid', 'name');

  return (
    <Select
      disabled={disabled}
      label={t('substitutions.availableRooms')}
      options={roomOptions}
      onChange={(option) => {
        const values = option as MultiValue<SelectOptionType>;
        onChange(values.map((v) => v.value as string));
      }}
      value={roomsData.rooms
        .filter((ro) => eventRoomUuids?.includes(ro.uuid))
        .map((ro) => ({ value: ro.uuid, label: ro.name }))}
      isMulti
      isClearable
      isSearchable
      name='available-rooms'
      menuPosition='fixed'
      error={error ?? undefined}
      className={styles['substitution-rooms-select']}
    />
  );
};
